'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Sparkles, ArrowRight, Clock, Zap, Lock, CheckCircle2, Brain, Smile, FlaskConical, Mail, Loader2, AlertCircle, RefreshCw, User, Phone, Calendar } from 'lucide-react';
import { getAnswersFromSession } from '@/lib/session';
import { createClient } from '@/lib/supabase/client';

// ─── Option ID → human-readable label maps ────────────────────────────────────

const WHY_NOW_LABEL_MAP: Record<string, string> = {
  q4_change: 'a significant life change or transition',
  q4_work: 'work or study pressure',
  q4_stuck: 'feeling stuck or like you\'re falling behind',
  q4_goal: 'a specific goal you\'re ready to go after',
  q4_curious: 'curiosity and a quiet sense that something could shift',
  'A big life change or transition': 'a significant life change or transition',
  'Work or study stress': 'work or study pressure',
  'Feeling stuck or behind': 'feeling stuck or like you\'re falling behind',
  'Wanting to go after a specific goal': 'a specific goal you\'re ready to go after',
  'Just curious / exploring': 'curiosity and a quiet sense that something could shift',
};

const GOAL_LABEL_MAP: Record<string, string> = {
  q1_anxiety: 'feeling calmer and more at ease day to day',
  q1_critic: 'building a kinder, more supportive inner voice',
  q1_confident: 'feeling more confident going after your goals',
  q1_clear: 'feeling clearer, more focused, and less overwhelmed',
  q1_sleep: 'sleeping better and switching off at night',
  q1_other: 'your personal goals',
};

const INNER_VOICE_LABEL_MAP: Record<string, string> = {
  q10_hard: "a voice that says you're not good enough or doing enough",
  q10_worry: 'overthinking and worrying about what might go wrong',
  q10_flat: 'feeling flat, low energy, or struggling to get motivated',
  q10_tense: 'feeling tense, stressed, or on edge',
  q10_compare: 'comparing yourself to others and feeling behind',
  q10_replay: 'replaying the day or worrying about tomorrow at night',
};

const TRIGGER_TIME_LABEL_MAP: Record<string, string> = {
  q11_morning: 'mornings, before the day has even started',
  q11_work: 'during work or study',
  q11_social: 'in social situations',
  q11_evening: 'evenings, when the day replays',
  q11_night: 'at night, before sleep',
  q11_always: 'throughout the day — it feels constant',
};

function humaniseWhyNow(raw: string): string {
  if (!raw || !raw.trim()) return '';
  return WHY_NOW_LABEL_MAP[raw.trim()] ?? raw.toLowerCase().trim();
}

function humaniseGoal(raw: string): string {
  if (!raw || !raw.trim()) return '';
  return GOAL_LABEL_MAP[raw.trim()] ?? raw.toLowerCase().trim();
}

function humaniseInnerVoice(raw: string): string {
  if (!raw || !raw.trim()) return '';
  return INNER_VOICE_LABEL_MAP[raw.trim()] ?? raw.toLowerCase().trim();
}

function humaniseTriggerTime(raw: string): string {
  if (!raw || !raw.trim()) return '';
  return TRIGGER_TIME_LABEL_MAP[raw.trim()] ?? raw.toLowerCase().trim();
}

// ─── Plan derivation ──────────────────────────────────────────────────────────

interface PlanData {
  heading: string;
  northStarStatement: string | null;
  patternInterpretation: string;
  whatAnswersTellUs: Array<{ insight: string; meaning: string }>;
  tags: string[];
  reset10s: string;
  reset60s: string;
  ifThenPlan: string;
  scienceNarrative: Array<{ title: string; body: string }>;
  whyThisWorks: Array<{ title: string; explanation: string }>;
}

function derivePlanFromAnswers(answers: Record<string, unknown> | null): PlanData {
  // ── Read ALL answers ──────────────────────────────────────────────────────
  const innerVoiceRaw = (answers?.q10_pattern as string) ?? (answers?.q10_inner_voice as string) ?? '';
  const triggerTimeRaw = (answers?.q11_loudest as string) ?? (answers?.q11_trigger_time as string) ?? '';
  const experience = (answers?.q12_experience as string) ?? (answers?.q12_meditation_experience as string) ?? '';
  const duration = (answers?.q13_time as string) ?? (answers?.q13_realistic_duration as string) ?? '';
  const whyNowRaw = (answers?.q4_why_now as string) ?? '';
  const goalRaw = (answers?.q1_focus as string) ?? (answers?.q1_primary_goal as string) ?? '';
  const desiredIdentityRaw = (answers?.q2_outcome as string) ?? '';
  const goalDetailText = (answers?.q1_focus_text as string) ?? '';
  const whyNowDetailText = (answers?.q4_why_now_text as string) ?? '';
  const specificSituation = (answers?.q19_situation as string) ?? '';
  const wishBelief = (answers?.q20_wish as string) ?? '';

  // ── Humanise values ───────────────────────────────────────────────────────
  const whyNow = humaniseWhyNow(whyNowRaw);
  const goalHuman = humaniseGoal(goalRaw);
  const innerVoiceLabel = humaniseInnerVoice(innerVoiceRaw);
  const triggerTimeLabel = humaniseTriggerTime(triggerTimeRaw);

  // ── Derive primary pattern ────────────────────────────────────────────────
  let pattern = 'GENERAL';
  if (innerVoiceRaw === 'q10_hard' || innerVoiceRaw.includes('hard on myself')) pattern = 'INNER_CRITIC';
  else if (innerVoiceRaw === 'q10_worry' || innerVoiceRaw.includes('worry and overthink')) pattern = 'ANXIOUS_OVERTHINKING';
  else if (innerVoiceRaw === 'q10_flat' || innerVoiceRaw.includes('flat or unmotivated')) pattern = 'LOW_MOOD';
  else if (innerVoiceRaw === 'q10_tense' || innerVoiceRaw.includes('tense and on edge')) pattern = 'TENSION';
  else if (innerVoiceRaw === 'q10_compare' || innerVoiceRaw.includes('compare myself')) pattern = 'COMPARISON';
  else if (innerVoiceRaw === 'q10_replay' || innerVoiceRaw.includes('replays the day')) pattern = 'NIGHT_RUMINATION';

  // ── Derive trigger window ─────────────────────────────────────────────────
  let trigger = 'GENERAL';
  if (triggerTimeRaw === 'q11_morning' || triggerTimeRaw.includes('Morning')) trigger = 'MORNING';
  else if (triggerTimeRaw === 'q11_work' || triggerTimeRaw.includes('During work')) trigger = 'WORKDAY';
  else if (triggerTimeRaw === 'q11_social' || triggerTimeRaw.includes('social')) trigger = 'SOCIAL';
  else if (triggerTimeRaw === 'q11_evening' || triggerTimeRaw.includes('Evenings')) trigger = 'EVENING';
  else if (triggerTimeRaw === 'q11_night' || triggerTimeRaw.includes('night')) trigger = 'NIGHT';

  const isMicroOnly = duration === 'q13_10s' || duration.includes('10-second');

  // ── Psychological interpretation of pattern ───────────────────────────────
  let patternInterpretation = '';
  let tags: string[] = [];
  let reset10s = '';
  let reset60s = '';

  if (pattern === 'INNER_CRITIC') {
    patternInterpretation = `What we're seeing is someone who holds themselves to a high standard and is ready to channel that drive more effectively. The inner critic has been trying to help — but there's a more powerful way forward. Your practice is built to replace that pattern with something that actually builds confidence and gets better results.`;
    tags = ['Self-belief', 'Confidence', 'Inner strength'];
    reset10s = 'Place one hand on your chest. Take one slow breath. Say quietly: "I am capable and I am moving forward." Allow a gentle smile — hold it for three breaths. Notice what shifts.';
    reset60s =
      'Close your eyes. Take three slow breaths. With each exhale, release one critical thought — not by arguing with it, just by letting it pass. Remind yourself of one small thing you did well today. End with a gentle breath and a quiet sense of your own capability.';
  } else if (pattern === 'ANXIOUS_OVERTHINKING') {
    patternInterpretation = `What we're seeing is a sharp, forward-thinking mind that's ready to direct its energy more intentionally. The overthinking has been trying to keep you safe — but your practice is designed to give that energy a better job: building genuine confidence and clarity rather than running scenarios.`;
    tags = ['Confidence', trigger === 'WORKDAY' ? 'Work clarity' : 'Calm focus', 'Forward momentum'];
    reset10s =
      'Breathe in for 4 counts, hold for 4, out for 6. Repeat once. Feel your feet on the floor. Let your jaw soften. Take one slow breath and say quietly: "I can handle this."';
    reset60s =
      'Pause what you are doing. Take five slow breaths, extending the exhale each time. Notice three things you can see around you. Return to your task with fresh eyes and a quiet sense of capability.';
  } else if (pattern === 'LOW_MOOD') {
    patternInterpretation = isMicroOnly
      ? `What we're seeing is someone who has been carrying a lot and is ready to build momentum again. Your practice starts with small wins that compound — tiny, achievable moments that remind you of your own capability and build forward momentum from the ground up.`
      : `What we're seeing is someone who has been carrying a lot and is ready to find a more sustainable rhythm. Your practice starts gently and builds — small, reliable moments of capability that compound into genuine confidence and energy over time.`;
    tags = ['Momentum', 'Self-worth', 'Gentle progress'];
    reset10s =
      'Stand up straight. Take one deep breath. Say quietly: "I am building something real." Allow a gentle smile — even if it feels strange. That\'s exactly when it works best.';
    reset60s =
      'Sit comfortably. Breathe slowly. Think of one moment — however small — when you felt capable. Let that feeling settle in your body for a few breaths. End with a quiet sense of your own forward direction.';
  } else if (pattern === 'NIGHT_RUMINATION' || trigger === 'NIGHT') {
    patternInterpretation = `What we're seeing is an active, engaged mind that's ready to learn how to rest as well as it thinks. Your practice gives your nervous system a reliable signal that the day is done — so you can wake up clearer, calmer, and more ready to move forward.`;
    tags = ['Rest', 'Clarity', 'Forward momentum'];
    reset10s =
      'Breathe out slowly, twice as long as your inhale. Let your jaw unclench. The day is done. You are safe. Take one slow breath and say quietly: "Tomorrow I begin again."';
    reset60s =
      'Lie or sit comfortably. Breathe slowly. Imagine placing each thought of the day into a box and closing the lid. You can return to them tomorrow. For now, rest. Close with a quiet sense of what you are building.';
  } else if (pattern === 'COMPARISON') {
    patternInterpretation = `What we're seeing is someone who knows what they want and is ready to stop measuring their progress against others. Your practice is designed to build a stable, internally-grounded confidence — one that doesn't depend on where anyone else is, and that grows stronger every day.`;
    tags = ['Own path', 'Self-belief', 'Confidence'];
    reset10s =
      'Take a breath. Remind yourself: "My path is my own and I am moving forward." Let the comparison thought pass like a cloud. Return to your direction.';
    reset60s =
      'Close your eyes. Breathe slowly. Bring to mind one quality you genuinely value in yourself. Let it feel real — not as a performance, but as a truth. End with a quiet sense of your own direction.';
  } else if (pattern === 'TENSION') {
    patternInterpretation = `What we're seeing is someone who has been pushing hard and is ready for a more sustainable rhythm. Your practice works at the body level first — releasing the tension that's been holding you back so you can move forward with more ease, clarity, and confidence.`;
    tags = ['Calm', 'Confidence', 'Sustainable energy'];
    reset10s =
      'Drop your shoulders. Unclench your jaw. Take one slow breath. Feel the ground beneath you. Say quietly: "I am steady and I am capable."';
    reset60s =
      'Scan your body from head to toe. Wherever you notice tension, breathe into it and let it soften on the exhale. Repeat three times. End with a quiet sense of your own steadiness.';
  } else {
    patternInterpretation = `What we're seeing is a readiness to build something more reliable — a daily practice that fits your real life and grows with you. Your plan starts with the fundamentals: small, consistent tools that compound into lasting confidence and change.`;
    tags = ['Self-belief', 'Confidence', 'Daily reset'];
    reset10s =
      'Take one slow, deliberate breath. Feel your feet on the floor. Say quietly: "I am capable and I am moving forward."';
    reset60s =
      'Find a comfortable position. Take five slow breaths, letting each exhale be a little longer than the inhale. Notice how your body feels. End with a quiet sense of your own capability.';
  }

  // ── Build "What your answers tell us" insights ────────────────────────────
  const whatAnswersTellUs: Array<{ insight: string; meaning: string }> = [];

  if (innerVoiceLabel) {
    const innerVoiceMeaning =
      pattern === 'INNER_CRITIC' ? 'This tells us you hold yourself to a high standard — and you\'re ready to channel that drive more effectively. Your practice replaces the inner critic with something that actually builds confidence and gets better results.'
      : pattern === 'ANXIOUS_OVERTHINKING' ? 'This tells us you have a sharp, forward-thinking mind that\'s been working too hard to stay safe. Your practice gives that energy a better job: building genuine confidence and clarity.'
      : pattern === 'LOW_MOOD' ? 'This tells us you\'ve been carrying a lot and are ready to build momentum again. Your practice starts gently and compounds — small moments of capability that rebuild your sense of what\'s possible.'
      : pattern === 'TENSION' ? 'This tells us you\'ve been pushing hard and are ready for a more sustainable rhythm. Your practice works at the body level first, releasing what\'s been holding you back.'
      : pattern === 'COMPARISON' ? 'This tells us you know what you want and are ready to stop measuring yourself against others. Your practice builds a stable, internally-grounded confidence that doesn\'t depend on where anyone else is.'
      : pattern === 'NIGHT_RUMINATION' ? 'This tells us your active mind hasn\'t been getting a clear signal that the day is done. Your practice creates that signal — so you can wake up clearer and more ready to move forward.'
      : 'This tells us you\'re ready to build something consistent. Your practice starts simple and grows with you.';
    whatAnswersTellUs.push({ insight: `What tends to get in the way: ${innerVoiceLabel}`, meaning: innerVoiceMeaning });
  }

  if (triggerTimeLabel) {
    const triggerMeaning =
      trigger === 'MORNING' ? 'Mornings are when the nervous system is most malleable — which means a brief morning practice has an outsized effect on the rest of your day. Your plan leads with this, setting you up to move through the day with more confidence and intention.'
      : trigger === 'WORKDAY' ? 'Work triggers are often about uncertainty and perceived performance. Your plan includes tools specifically designed for in-the-moment resets that help you return to a confident, capable state quickly.'
      : trigger === 'NIGHT' ? 'Night-time is when the mind tries to process everything it didn\'t have time for during the day. Your plan includes a specific wind-down practice to help you close the day with intention — protecting your energy and confidence for tomorrow.'
      : trigger === 'SOCIAL' ? 'Social triggers often connect to deeper beliefs about how others see us. Your plan includes tools to help you return to yourself quickly in those moments — grounded in your own sense of capability.'
      : trigger === 'EVENING' ? 'Evenings are when the day\'s experiences tend to replay. Your plan includes an evening practice specifically designed to help you close the day with intention and self-belief intact.'
      : 'When a pattern feels constant, the most effective approach is building a reliable baseline — a daily practice that gradually shifts the default state towards confidence and calm.';
    whatAnswersTellUs.push({ insight: `When it tends to show up: ${triggerTimeLabel}`, meaning: triggerMeaning });
  }

  if (goalDetailText && goalDetailText.trim().length > 5) {
    whatAnswersTellUs.push({
      insight: 'What you shared about your goal',
      meaning: `This is exactly the kind of context that makes a practice feel relevant rather than generic. Your if-then plan and daily tools are built specifically around moments like this — helping you move towards what you actually want.`,
    });
  }

  if (specificSituation && specificSituation.trim().length > 5) {
    whatAnswersTellUs.push({
      insight: 'The specific goal or situation you described',
      meaning: `This is your north star — the specific place where your practice will make the most difference. Every tool in your plan is designed to help you show up more confidently and capably in exactly these moments.`,
    });
  }

  // ── Build if-then plan ────────────────────────────────────────────────────
  const triggerDescription = specificSituation && specificSituation.trim().length > 5
    ? specificSituation.trim()
    : triggerTimeLabel
    ? `the pattern shows up ${triggerTimeLabel}`
    : trigger === 'WORKDAY' ? 'a stressful work moment hits'
    : trigger === 'NIGHT' ? 'I\'m lying awake with racing thoughts'
    : trigger === 'MORNING' ? 'I wake with anxiety or dread'
    : trigger === 'SOCIAL' ? 'a social situation triggers the pattern'
    : trigger === 'EVENING'? 'the evening replay starts' :'the pattern shows up';

  const ifThenPlan = `If ${triggerDescription}, then I will take one slow breath, introduce a deliberate smile, and say to myself: "I know what to do."`;

  // ── North star statement ──────────────────────────────────────────────────
  let northStarStatement: string | null = null;
  if (desiredIdentityRaw && desiredIdentityRaw.trim().length > 3) {
    northStarStatement = desiredIdentityRaw.trim();
  }

  // ── Science narrative ─────────────────────────────────────────────────────
  const scienceNarrative: Array<{ title: string; body: string }> = [
    {
      title: 'Breath + Smile + Belief',
      body: 'Every reset in your plan pairs a slow breath, a gentle smile, and a brief self-affirming thought. Repeated over time, this can help your body feel calmer, your mind feel less defensive, and your response feel more capable and intentional. Slow-paced breathing has evidence for reducing arousal and improving emotion regulation. Facial feedback research suggests smiling can modestly improve positive affect. Self-affirmation can reduce defensiveness, improve openness, and broaden perspective under pressure.',
    },
    {
      title: 'Why brief practices work',
      body: 'Research consistently shows that brief, consistent practices are more effective than occasional long sessions for building lasting habits. Your plan is designed around this: small, daily tools that compound over time. The goal is not perfection — it is repetition.',
    },
    {
      title: 'Implementation intentions',
      body: 'Deciding in advance exactly when and how you will act — "if X happens, then I will do Y" — significantly increases the likelihood of following through compared to vague intentions. This is one of the most evidence-supported behaviour change tools available. Your if-then plan is built specifically around your trigger situation.',
    },
  ];

  // ── Why this works ────────────────────────────────────────────────────────
  const whyThisWorks: Array<{ title: string; explanation: string }> = [
    {
      title: pattern === 'INNER_CRITIC' ? 'Self-compassion builds better results than self-criticism' : pattern === 'ANXIOUS_OVERTHINKING' ? 'Extended exhales calm the nervous system fast' : pattern === 'LOW_MOOD' ? 'Small wins rebuild momentum and confidence' : pattern === 'TENSION' ? 'Body and mind calm each other' : pattern === 'COMPARISON' ? 'Values-based focus builds stable confidence' : pattern === 'NIGHT_RUMINATION' ? 'Cognitive defusion reduces the power of rumination' : 'Consistency builds lasting confidence',
      explanation: pattern === 'INNER_CRITIC' ? 'Studies show that treating yourself with kindness — rather than harsh self-judgement — leads to greater motivation, resilience, and follow-through over time. Self-compassion is not self-indulgence; it is a more effective strategy for achieving your goals.' : pattern === 'ANXIOUS_OVERTHINKING' ? 'Breathing out for longer than you breathe in activates the parasympathetic nervous system — your body\'s natural calming response — within seconds. Even one minute of this practice can shift your state and help you think more clearly.' : pattern === 'LOW_MOOD' ? 'Research on motivation shows that small, achievable actions create a sense of progress that builds naturally into larger change. Your plan starts small on purpose — building momentum and confidence from the ground up.' : pattern === 'TENSION' ? 'Physical tension and mental stress reinforce each other. Releasing tension in the body — through breath and body-scan — directly reduces mental stress, often faster than cognitive approaches, freeing up energy for what matters.' : pattern === 'COMPARISON' ? 'Redirecting attention from what others have to what you personally value and where you are going has been shown to reduce the negative impact of social comparison and build a more stable, confident sense of self.' : pattern === 'NIGHT_RUMINATION' ? 'Techniques that help you observe thoughts rather than engage with them — like the box visualisation — reduce their emotional charge significantly, making it easier to let them go and wake up ready to move forward.' : 'Research consistently shows that brief, daily practices build stronger habits than occasional longer sessions. Your plan is designed around this principle — building confidence and capability through consistent small actions.',
    },
    {
      title: 'The triple cue: a practical reset',
      explanation: 'Every reset in your plan pairs a slow breath, a gentle smile, and a brief self-affirming thought. Repeated over time, this can help your body feel calmer, your mind feel less defensive, and your response feel more capable and intentional. The value is in the repetition — each time you use it, it becomes a little more automatic.',
    },
  ];

  // ── Heading ───────────────────────────────────────────────────────────────
  const heading = goalHuman
    ? `Here's what we're seeing — and how we'll help` : "Here's what we're seeing so far";

  return {
    heading,
    northStarStatement,
    patternInterpretation,
    whatAnswersTellUs,
    tags,
    reset10s,
    reset60s,
    ifThenPlan,
    scienceNarrative,
    whyThisWorks,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PlanPreviewContent() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<PlanData | null>(null);

  // Magic link state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);

  // User details state
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [detailsError, setDetailsError] = useState('');

  useEffect(() => {
    // Detect OTP expired / access_denied errors sent back via URL hash or query params
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      const search = window.location.search;
      const isHashError = hash.includes('error=access_denied') || hash.includes('error_code=otp_expired') || hash.includes('error=');
      const isQueryError = search.includes('error=access_denied') || search.includes('error_code=otp_expired') || search.includes('auth_error=1');
      if (isHashError || isQueryError) {
        setLinkExpired(true);
        // Clean the URL so the error params don't persist on refresh
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const answers = getAnswersFromSession();
      setPlan(derivePlanFromAnswers(answers));
      setLoading(false);
    }, 2200);
    return () => clearTimeout(timer);
  }, []);

  async function handleSaveAndGenerate(e: React.FormEvent) {
    e.preventDefault();
    setEmailError('');
    setDetailsError('');

    // Validate name
    if (!name.trim()) {
      setDetailsError('Please enter your first name');
      return;
    }

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSending(true);

    try {
      // Persist questionnaire answers + plan data to localStorage so they survive the magic link redirect
      const answers = getAnswersFromSession();
      const enrichedAnswers = {
        ...(answers ?? {}),
        q5_name: name.trim(),
        q_age: age.trim(),
        q_whatsapp: whatsapp.trim(),
      };
      localStorage.setItem('blv_pending_answers', JSON.stringify(enrichedAnswers));
      if (plan) {
        localStorage.setItem('blv_pending_plan_preview', JSON.stringify(plan));
      }
      localStorage.setItem('blv_pending_email', email.toLowerCase().trim());

      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? window.location.origin;

      const { error } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase().trim(),
        options: {
          emailRedirectTo: `${siteUrl}/auth/callback?next=/your-reset-practice-home`,
          shouldCreateUser: true,
        },
      });

      if (error) {
        setEmailError(error.message ?? 'Something went wrong. Please try again.');
        setSending(false);
        return;
      }

      setSent(true);
    } catch {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#FAF8F5]">
        <AppLogo size={48} className="mb-6 pulse-soft" />
        <h2 className="text-2xl font-bold text-[#1C1917] mb-3 text-center">
          Reading your pattern…
        </h2>
        <p className="text-[#6B6560] text-center max-w-xs leading-relaxed mb-8">
          We&apos;re interpreting your responses and building your personalised plan. This takes just a moment.
        </p>
        <div className="w-48 h-1.5 bg-[#EAE6E0] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#2A9D8F] to-[#52B788] rounded-full"
            style={{ animation: 'loadingBar 2s ease-in-out forwards' }}
          />
        </div>
        <style>{`
          @keyframes loadingBar {
            0% { width: 0%; }
            40% { width: 60%; }
            80% { width: 85%; }
            100% { width: 100%; }
          }
        `}</style>
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#EAE6E0]">
        <div className="max-w-screen-sm mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-welcome" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg text-[#1C1917]">blv</span>
          </Link>
          <span className="text-xs font-medium text-[#52B788] bg-[#52B788]/10 px-3 py-1 rounded-full">
            Your plan is ready
          </span>
        </div>
      </div>

      <main className="max-w-screen-sm mx-auto px-6 pt-10 pb-36 fade-in">

        {/* ── North star framing ──────────────────────────────────────────── */}
        {plan.northStarStatement && (
          <div className="mb-8 rounded-2xl p-6 border border-[#2A9D8F]/25 bg-gradient-to-br from-[#2A9D8F]/06 to-[#52B788]/04">
            <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-2">
              Your north star
            </p>
            <p className="text-base font-semibold text-[#1C1917] leading-snug italic">
              &ldquo;{plan.northStarStatement}&rdquo;
            </p>
            <p className="text-xs text-[#9B9590] mt-2 leading-relaxed">
              This is the version of you your practice is building towards. Every tool in your plan is designed to close the gap between where you are now and where you are going.
            </p>
          </div>
        )}

        {/* ── Heading ─────────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-[#2A9D8F]/10 rounded-full px-4 py-1.5 text-sm font-semibold text-[#2A9D8F] mb-5">
            <Sparkles size={14} />
            Your personalised practice
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-5">
            {plan.heading}
          </h1>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {plan.tags.map((tag) => (
              <span key={`preview-tag-${tag}`} className="tag-chip">{tag}</span>
            ))}
          </div>
        </div>

        {/* ── Pattern interpretation card ──────────────────────────────────── */}
        <div className="card-elevated p-6 sm:p-8 mb-5">
          <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-4">
            What we&apos;re seeing
          </p>
          <p className="text-[#4A4540] leading-relaxed text-base">
            {plan.patternInterpretation}
          </p>
        </div>

        {/* ── What your answers tell us ────────────────────────────────────── */}
        {plan.whatAnswersTellUs.length > 0 && (
          <div className="card-elevated p-6 sm:p-8 mb-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl bg-[#52B788]/10 flex items-center justify-center flex-shrink-0">
                <Brain size={15} className="text-[#52B788]" />
              </div>
              <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest">
                What your answers tell us
              </p>
            </div>
            <div className="space-y-5">
              {plan.whatAnswersTellUs.map((item, i) => (
                <div key={`insight-${i}`} className="border-l-2 border-[#2A9D8F]/30 pl-4">
                  <p className="text-sm font-semibold text-[#1C1917] mb-1">{item.insight}</p>
                  <p className="text-sm text-[#6B6560] leading-relaxed">{item.meaning}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Why this works ───────────────────────────────────────────────── */}
        {plan.whyThisWorks.length > 0 && (
          <div className="card-elevated p-6 sm:p-8 mb-5">
            <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-4">
              Why this works for you
            </p>
            <div className="space-y-4">
              {plan.whyThisWorks.map((item, i) => (
                <div key={`why-${i}`} className="flex gap-3">
                  <CheckCircle2 size={16} className="text-[#52B788] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#1C1917] mb-0.5">{item.title}</p>
                    <p className="text-sm text-[#6B6560] leading-relaxed">{item.explanation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Two reset blocks ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="reset-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                <Zap size={15} className="text-[#2A9D8F]" />
              </div>
              <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest">
                Your 10-second reset
              </p>
            </div>
            <p className="text-sm text-[#4A4540] leading-relaxed">
              {plan.reset10s}
            </p>
          </div>

          <div className="reset-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-[#52B788]/10 flex items-center justify-center flex-shrink-0">
                <Clock size={15} className="text-[#52B788]" />
              </div>
              <p className="text-xs font-semibold text-[#52B788] uppercase tracking-widest">
                Your 60-second reset
              </p>
            </div>
            <p className="text-sm text-[#4A4540] leading-relaxed">
              {plan.reset60s}
            </p>
          </div>
        </div>

        {/* ── If-then plan ─────────────────────────────────────────────────── */}
        <div className="rounded-2xl p-5 mb-5 border border-[#2A9D8F]/20" style={{ background: 'rgba(42,157,143,0.04)' }}>
          <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-2">
            Your if-then plan
          </p>
          <p className="text-sm font-medium text-[#1C1917] leading-relaxed italic mb-2">
            &ldquo;{plan.ifThenPlan}&rdquo;
          </p>
          <p className="text-xs text-[#9B9590] leading-relaxed">
            Implementation intentions — specific if-then plans — are one of the most evidence-supported ways to turn intentions into action. The more specific the trigger, the more automatic the response becomes.
          </p>
        </div>

        {/* ── The science behind your plan ────────────────────────────────── */}
        <div className="card-elevated p-6 sm:p-8 mb-5">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <FlaskConical size={15} className="text-amber-600" />
            </div>
            <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest">
              The science behind your plan
            </p>
          </div>
          <div className="space-y-5">
            {plan.scienceNarrative.map((item, i) => (
              <div key={`science-${i}`}>
                <p className="text-sm font-semibold text-[#1C1917] mb-1">{item.title}</p>
                <p className="text-sm text-[#6B6560] leading-relaxed">{item.body}</p>
                {i < plan.scienceNarrative.length - 1 && (
                  <div className="mt-4 border-t border-[#EAE6E0]" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Deliberate smile callout ─────────────────────────────────────── */}
        <div className="rounded-2xl p-5 mb-8 border border-[#52B788]/25 bg-gradient-to-br from-[#52B788]/06 to-[#2A9D8F]/04">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#52B788]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Smile size={16} className="text-[#52B788]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#1C1917] mb-1">Breath + Smile + Belief</p>
              <p className="text-sm text-[#4A4540] leading-relaxed">
                Every reset in your plan pairs a slow breath, a gentle smile, and a brief self-affirming thought. Repeated over time, this can help your body feel calmer, your mind feel less defensive, and your response feel more capable and intentional.
              </p>
            </div>
          </div>
        </div>

        {/* ── Full plan teaser ─────────────────────────────────────────────── */}
        <div
          className="rounded-2xl p-6 mb-8 border border-dashed border-[#2A9D8F]/30"
          style={{ background: 'rgba(42,157,143,0.04)' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Lock size={16} className="text-[#2A9D8F]" />
            <p className="text-sm font-semibold text-[#2A9D8F]">Your full personalised plan</p>
          </div>
          <p className="text-sm text-[#6B6560] leading-relaxed mb-3">
            Save this to unlock your complete Personalised Daily Reset Plan — including a 7-day
            starter programme, morning and evening practices, and two guided meditation scripts
            written specifically for your pattern and goals.
          </p>
          <ul className="space-y-1.5 mb-3">
            {[
              'Morning & evening meditation scripts (8–12 min each)',
              '7-day starter plan built around your pattern',
              'Evidence-informed explanations for every tool',
              'A personalised if-then plan for your main trigger',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-xs text-[#6B6560]">
                <CheckCircle2 size={12} className="text-[#52B788] flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-xs text-[#9B9590] italic">
            Later, you&apos;ll be able to generate sessions in a voice that sounds like your own,
            so your practice feels even more personal.
          </p>
        </div>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#FAF8F5]/95 backdrop-blur-md border-t border-[#EAE6E0] px-6 py-4">
        <div className="max-w-screen-sm mx-auto">
          {sent ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="flex items-center gap-2 text-[#52B788]">
                <CheckCircle2 size={20} />
                <span className="font-semibold text-base">Check your email</span>
              </div>
              <p className="text-xs text-[#6B6560] text-center">
                We&apos;ve sent a magic link to <span className="font-medium text-[#1C1917]">{email}</span>. Click it to save your plan and access your dashboard.
              </p>
            </div>
          ) : linkExpired ? (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle size={20} />
                <span className="font-semibold text-base">That link has expired</span>
              </div>
              <p className="text-xs text-[#6B6560] text-center">
                Magic links expire after a short time for security. Enter your email below to get a fresh one.
              </p>
              <form onSubmit={async (e) => { e.preventDefault(); setLinkExpired(false); await handleSaveAndGenerate(e); }} className="w-full space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9590]" />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                      className="w-full bg-white border border-amber-300 rounded-xl pl-9 pr-3 py-3 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
                      disabled={sending}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-primary px-4 py-3 text-sm whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <RefreshCw size={15} />
                        Resend
                      </>
                    )}
                  </button>
                </div>
                {emailError && (
                  <p className="text-xs text-red-500">{emailError}</p>
                )}
              </form>
            </div>
          ) : (
            <form onSubmit={handleSaveAndGenerate} className="space-y-3">
              {/* Reason to save */}
              <div className="text-center pb-1">
                <p className="text-sm font-semibold text-[#1C1917]">Save your plan &amp; unlock your full practice</p>
                <p className="text-xs text-[#6B6560] mt-0.5 leading-relaxed">
                  Your personalised morning &amp; evening meditations, 7-day starter plan, and guided scripts — all built around your pattern. Free, no password needed.
                </p>
              </div>

              {/* Name + Age row */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9590]" />
                  <input
                    type="text"
                    placeholder="First name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setDetailsError(''); }}
                    className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-9 pr-3 py-2.5 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
                    disabled={sending}
                  />
                </div>
                <div className="relative w-24">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9590]" />
                  <input
                    type="number"
                    placeholder="Age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    min={13}
                    max={120}
                    className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-9 pr-3 py-2.5 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
                    disabled={sending}
                  />
                </div>
              </div>

              {/* WhatsApp */}
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9590]" />
                <input
                  type="tel"
                  placeholder="WhatsApp number (optional)"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-9 pr-3 py-2.5 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
                  disabled={sending}
                />
              </div>

              {detailsError && (
                <p className="text-xs text-red-500">{detailsError}</p>
              )}

              {/* Email + CTA */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9590]" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-9 pr-3 py-2.5 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
                    disabled={sending}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary px-4 py-2.5 text-sm whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
                >
                  {sending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      Save &amp; Generate
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </div>
              {emailError && (
                <p className="text-xs text-red-500">{emailError}</p>
              )}
              <p className="text-center text-xs text-[#9B9590]">
                Free. No password. No card needed. We&apos;ll email you a magic link.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}